import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import config from '../config';
import authService from '../services/authService';
import { AppContext } from '../App';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' }
];

function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const now = new Date();
  const seconds = Math.floor((now - then) / 1000);
  if (Number.isNaN(seconds) || seconds < 0) return new Date(iso).toLocaleString();
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString();
}

function TypeBadge({ type }) {
  const label = type === 'api_key' ? 'API key' : 'License grant';
  const cls =
    type === 'api_key'
      ? 'bg-indigo-900/40 text-indigo-200 border-indigo-700/50'
      : 'bg-amber-900/40 text-amber-200 border-amber-700/50';
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>{label}</span>
  );
}

function SourceBadge({ source }) {
  if (!source) return null;
  const label =
    source === 'provisioning_bundle' ? 'CRM' : source === 'dashboard' ? 'dashboard' : source;
  return (
    <span className="text-[0.65rem] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
      {label}
    </span>
  );
}

function Details({ event }) {
  if (event.type === 'api_key') {
    const parts = [];
    if (event.details.nickname) parts.push(event.details.nickname);
    if (event.details.planName) parts.push(`plan: ${event.details.planName}`);
    return <span className="text-sm text-slate-400">{parts.join(' · ') || '—'}</span>;
  }
  // license_grant
  const parts = [];
  if (typeof event.details.seats === 'number') parts.push(`${event.details.seats} seats`);
  if (event.details.validUntil) {
    parts.push(`until ${new Date(event.details.validUntil).toLocaleDateString()}`);
  }
  if (event.details.status && event.details.status !== 'active') {
    parts.push(event.details.status);
  }
  return <span className="text-sm text-slate-400">{parts.join(' · ') || '—'}</span>;
}

const IssuanceLog = () => {
  const { selectedTeam } = useContext(AppContext);
  const teamId = selectedTeam?.teamId;

  const [days, setDays] = useState(30);
  const [actorId, setActorId] = useState('');
  const [customerId, setCustomerId] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let timer;
    if (loading) timer = setTimeout(() => setShowLoading(true), 150);
    else setShowLoading(false);
    return () => clearTimeout(timer);
  }, [loading]);

  const fetchLog = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ vendorTeamId: teamId, days: String(days) });
      if (actorId) params.set('actorId', actorId);
      if (customerId) params.set('customerId', customerId);
      const res = await fetch(
        `${config.apiBaseUrl}/api/reporting/issuance-log?${params.toString()}`,
        { headers: { ...authService.getAuthHeaders() } }
      );
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new Error(body?.error || `Failed to load log (HTTP ${res.status})`);
      }
      setData(body);
    } catch (err) {
      console.error('Failed to load issuance log:', err);
      setError(err.message || 'Failed to load issuance log');
    } finally {
      setLoading(false);
    }
  }, [teamId, days, actorId, customerId]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const events = useMemo(() => data?.events ?? [], [data]);
  const actors = useMemo(() => data?.actors ?? [], [data]);
  const customers = useMemo(() => data?.customers ?? [], [data]);

  if (!teamId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-slate-400">
        Select a team to see its issuance log.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-100 mb-1">Credential Issuance Log</h2>
          <p className="text-sm text-slate-400 max-w-2xl">
            Audit trail of every API key and license grant issued under
            <strong> {selectedTeam.teamName}</strong>. Filter by date range, who
            issued the credential, or which customer received it.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-6">
        <label className="text-sm text-slate-400 flex items-center gap-2">
          Range:
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded px-2 py-1"
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-400 flex items-center gap-2">
          Issued by:
          <select
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded px-2 py-1 min-w-[180px]"
          >
            <option value="">Anyone</option>
            {actors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.email}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-400 flex items-center gap-2">
          Customer:
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded px-2 py-1 min-w-[180px]"
          >
            <option value="">Any customer</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {(actorId || customerId) && (
          <button
            onClick={() => {
              setActorId('');
              setCustomerId('');
            }}
            className="text-xs text-indigo-400 hover:text-indigo-300"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-950/30 border border-red-800/50 text-red-300 px-4 py-3 rounded-lg mb-4" role="alert">
          <strong className="font-semibold">Error:</strong>
          <span className="ml-2">{error}</span>
        </div>
      )}

      {loading && showLoading ? (
        <div className="flex items-center text-slate-400">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mr-3" />
          Loading issuance log…
        </div>
      ) : events.length === 0 && !loading ? (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          <h2 className="text-base font-semibold mb-1 text-slate-100">No issuances in this window</h2>
          <p className="text-slate-400 text-sm">
            No API keys or license grants have been issued in the selected range. Try widening the range or clearing filters.
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 overflow-x-auto sm:rounded-lg">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">When</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Issuer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {events.map((e) => (
                <tr key={`${e.type}-${e.resourceId}`} className="hover:bg-slate-800">
                  <td
                    className="px-6 py-3 whitespace-nowrap text-sm text-slate-300"
                    title={e.at ? new Date(e.at).toLocaleString() : ''}
                  >
                    {relativeTime(e.at)}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <TypeBadge type={e.type} />
                      <SourceBadge source={e.source} />
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-300">
                    {e.actor?.email || <span className="text-slate-600">unknown</span>}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-200">
                    {e.customer?.name || '—'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-slate-300">
                    {e.product?.name || '—'}
                  </td>
                  <td
                    className="px-6 py-3 text-sm text-slate-400 max-w-[240px]"
                    title={
                      e.type === 'api_key'
                        ? [e.details.nickname, e.details.planName && `plan: ${e.details.planName}`]
                            .filter(Boolean)
                            .join(' · ')
                        : [
                            typeof e.details.seats === 'number' && `${e.details.seats} seats`,
                            e.details.validUntil &&
                              `until ${new Date(e.details.validUntil).toLocaleDateString()}`,
                            e.details.status
                          ]
                            .filter(Boolean)
                            .join(' · ')
                    }
                  >
                    <div className="truncate">
                      <Details event={e} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IssuanceLog;
